import React, { createContext, useContext, useCallback } from 'react';
import {
  FiAlertCircle,
  FiCheckCircle,
  FiInfo,
  FiXCircle,
} from 'react-icons/fi';
import { toast, ToastOptions, TypeOptions, Slide } from 'react-toastify';

export interface Notification {
  id?: string;
  title: string;
  message: string;
  type: 'success' | 'error' | 'warning' | 'danger' | 'info';
  duration?: number;
}

const mapTypeToToastType = (type: Notification['type']): TypeOptions => {
  switch (type) {
    case 'danger':
      return 'error';
    case 'error':
      return 'error';
    default:
      return type;
  }
};

interface NotificationContextType {
  addNotification: (notification: Omit<Notification, 'id'>) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined
);

export const useNotification = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      'useNotification must be used within a NotificationProvider'
    );
  }
  return context;
};

interface NotificationProviderProps {
  children: React.ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children,
}) => {
  const NotificationContent = useCallback(
    ({
      title,
      message,
      type,
    }: {
      title: string;
      message: string;
      type: Notification['type'];
    }) => (
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 pt-1">
          {type === 'success' && <FiCheckCircle size={20} />}
          {(type === 'error' || type === 'danger') && <FiXCircle size={20} />}
          {type === 'warning' && <FiAlertCircle size={20} />}
          {type === 'info' && <FiInfo size={20} />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold">{title}</div>
          <div className="text-sm">{message}</div>
        </div>
      </div>
    ),
    []
  );

  const addNotification = useCallback(
    (notification: Omit<Notification, 'id'>) => {
      const toastType = mapTypeToToastType(notification.type);

      const defaultOptions: ToastOptions = {
        type: toastType,
        position: 'bottom-right',
        autoClose: 5000,
        theme: 'dark',
        style: { minWidth: '420px' },
        transition: Slide,
      };

      toast(
        <NotificationContent
          title={notification.title}
          message={notification.message}
          type={notification.type}
        />,
        {
          ...defaultOptions,
          autoClose: notification.duration || defaultOptions.autoClose,
        }
      );
    },
    [NotificationContent]
  );

  const value: NotificationContextType = {
    addNotification,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
